// https://vuex.vuejs.org/en/actions.html
import axios from "axios"

// 正規の呼び出しであることの手がかりとしてサーバーへ送る（DMS-1261 の認可判定が
// 「UA と Referer は偽装されていたので使わず、X-Client-Version の有無を見る」という
// 方針のため）。値そのものは判定に使われないが、経路の切り分けにログへ残る。
const CLIENT_VERSION = `pre-checkin/${process.env.VUE_APP_VERSION || "0.0.0"}`

// 資格情報付きの共通ヘッダ。token が空なら Authorization を付けない（"Bearer " だけを送らない）。
// plugins/axios.js の共通ヘッダは localStorage の token を前提にしているが、それを書く箇所が
// 無いので機能していない。各アクションが自分の token / guestToken を明示的に付ける方針で統一する。
const authHeaders = (token) => {
  const headers = { "X-Client-Version": CLIENT_VERSION }
  if (token) headers.Authorization = "Bearer " + token
  return headers
}

// 引き換え系（DMS-1268-E の引き継ぎコード / DMS-1268-F の同行者招待）で共通の後処理。
//
// トークンを受け取ったあと reserved/user を引いて本人を確かめ、guestToken を立てる。
// personLogin と同じ形にするため、順序も同じにしてある（guestReserve が先。理由は
// commitGuest のコメント）。
const exchangeGuestToken = (commit, url, body) =>
  new Promise((resolve, reject) => {
    commit("guestLogout")
    axios
      .post(window.appApiUrl + url, body, {
        headers: { "X-Client-Version": CLIENT_VERSION },
      })
      .then((res) => {
        const accessToken = res.data.accessToken
        axios
          .get(window.appApiUrl + "reserved/user", {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          })
          .then((resRsv) => {
            // 同行者のトークンでは persons が本人だけになる（DMS-1273）。
            // 代表者のトークンなら全員返るので、いずれにせよ本人を id で引く
            const persons = resRsv.data.persons || []
            const personItem = persons.find(
              (item) => item.id === resRsv.data.id
            )
            if (!personItem) {
              reject(new Error("Guest is not existed or deleted!"))
              return
            }
            // guestToken より**先**に commit する。
            // - 引き換えの URL には userId が載らないので、画面側が mounted で置いた
            //   guestReserve は userId が null のままになる。401 のあとの復帰先が
            //   `?userId=null` にならないよう、実値で上書きする
            // - guestReserve は guestAuthStatus を false に戻す。guestToken は
            //   `if(!state.guestAuthStatus)` の中でしか値を入れないので、
            //   先に入れ替えておかないと二度目のログインで token が入らない
            commit("guestReserve", {
              userId: personItem.id,
              rsvId: personItem.bookingId || resRsv.data.booking.id,
            })
            commit("guestToken", {
              id: personItem.id,
              access_token: accessToken,
              ...personItem,
            })
            resolve(resRsv)
          })
          .catch((err) => {
            reject(err)
          })
      })
      .catch((err) => {
        reject(err)
      })
  })

// 署名の取得・保存で 401 を受けたときの戻り先（代表者の確認画面。getPersonPassport と同じ）。
const signatureUnauthorizedRedirect = (state) =>
  state.bookingId ? "/confirm/" + state.bookingId : "/error-page"

// The login action passes vuex commit helper that we will use to trigger mutations.
export default {
  login({ commit }, data) {
    commit("clearForm")

    return new Promise((resolve, reject) => {
      const dataPost = { id: data.rsvId, password: data.password }
      axios
        .post(window.appApiUrl + "reserved/login", dataPost)
        .then((res) => {
          const token = res.data.access_token
          commit("auth_success", { token })
          resolve(res)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },

  // DMS-1268-E
  //
  // KIOSK の QR に載っていた資格情報を、1回限りの引き継ぎコードに置き換える。
  // 代表者は `confirm/{bookingId}?password={平文}`、同行者は
  // `change-password/{bookingId}?userId={宿泊者ID}` で入っていた。
  //
  // コードは URL のフラグメントから取る（サーバーへ送られないので Referer にも
  // アクセスログにも残らない）。**1回限り**なので、失敗しても再試行はしない。
  //
  // 代表者用。パスワード認証の login と同じく state.token を立てる。
  handoffLoginOwner({ commit }, { code }) {
    commit("clearForm")

    return new Promise((resolve, reject) => {
      axios
        .post(window.appApiUrl + "reserved/handoffLogin", { code })
        .then((res) => {
          commit("auth_success", { token: res.data.accessToken })
          resolve(res)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },

  // 同行者用。personLogin と同じく guestToken を立てる。
  handoffLoginGuest({ commit }, { code }) {
    return exchangeGuestToken(commit, "reserved/handoffLogin", { code })
  },

  // DMS-1268-F
  //
  // 代表者が同行者へ共有する招待。従来は
  // `change-password/{bookingId}?userId={宿泊者ID}` を送っていたので、URL を受け取った
  // 誰でも、秘密を何も持たずに入れた。
  //
  // **発行は共有ボタンを押す前に済ませておく必要がある。** 共有は navigator.share() で
  // OS の共有シートを開くが、これはユーザー操作の直後にしか呼べない（間に API の
  // 往復を挟むと、iOS では開かないことがある）。そのため代表者の画面は、同行者一覧が
  // 出た時点で人数分をまとめて発行して持っておく。
  // サーバー側も、発行し直しで前の招待を失効させない作りにしてある（送信済みの
  // リンクを壊さないため。api-nest #2524）。
  issueInvite({ state }, { personId }) {
    return axios.post(
      window.appApiUrl + "reserved/person/" + personId + "/invite",
      {},
      {
        headers: {
          Authorization: "Bearer " + state.token,
          "X-Client-Version": CLIENT_VERSION,
        },
      }
    )
  },

  // 招待を引き換えて同行者として入る。
  inviteLoginGuest({ commit }, { token }) {
    return exchangeGuestToken(commit, "reserved/inviteLogin", { token })
  },

  // BOK002
  getReserved({ commit, state }) {
    return new Promise((resolve, reject) => {
      axios
        .get(window.appApiUrl + "reserved/user", {
          headers: {
            Authorization: "Bearer " + state.token,
          },
        })
        .then((res) => {
          const reserve = res.data
          commit("reserve_data", { reserve })
          commit("reserve_guests", { reserve })
          const user = reserve.persons.find((item) => item.isOwner === true)
          commit("reserve_user", {
            ...user,
          })
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              // window.location.href = "/confirm/" + state.bookingId;
            } else {
              window.location.href = "/error-page"
            }
          }
          reject(err.response)
        })
    })
  },

  // BOK008
  getPersonPassport({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .get(window.appApiUrl + "reserved/personPassport/" + data.id, {
          headers: {
            Authorization: "Bearer " + state.token,
          },
        })
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              window.location.href = "/confirm/" + state.bookingId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },
  guestGetPersonPassport({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .get(window.appApiUrl + "reserved/personPassport/" + data.id, {
          headers: {
            Authorization: "Bearer " + state.guestToken,
          },
        })
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.guestReserve) {
              window.location.href =
                "/change-password/" +
                state.guestReserve.rsvId +
                "?userId=" +
                state.guestReserve.userId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  //BOK009
  uploadPersonPassport({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .post(
          window.appApiUrl + "reserved/personPassport/" + data.id,
          data.form,
          {
            headers: {
              // 'Content-Type': 'multipart/form-data',
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + state.token,
            },
          }
        )
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.reserve) {
              window.location.href = "/confirm/" + state.reserve.booking.id
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  guestUploadPersonPassport({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .post(
          window.appApiUrl + "reserved/personPassport/" + data.id,
          data.form,
          {
            headers: {
              // 'Content-Type': 'multipart/form-data',
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + state.guestToken,
            },
          }
        )
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.guestReserve) {
              window.location.href =
                "/change-password/" +
                state.guestReserve.rsvId +
                "?userId=" +
                state.guestReserve.userId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK004
  addReservation({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .post(window.appApiUrl + "reserved/person", data, {
          headers: {
            Authorization: "Bearer " + state.token,
          },
        })
        .then((res) => {
          commit("add_guest", res.data)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              window.location.href = "/confirm/" + state.bookingId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK005
  updateReservation({ state, commit }, data) {
    if (data.occupation) {
      data.occupation = parseInt(data.occupation)
    }
    if (data.birthDate == "Invalid date") {
      data.birthDate = null
    }
    if (data.gender) {
      data.gender = parseInt(data.gender)
    }
    return new Promise((resolve, reject) => {
      axios
        .put(
          window.appApiUrl + "reserved/person/" + data.id,
          {
            name: data.name || undefined,
            address: data.address || null,
            occupation: data.occupation || null,
            otherOccupationName: data.otherOccupationName,
            postcode: data.postcode || null,
            birthDate: data.birthDate || null,
            gender: data.gender || null,
            nickName: data.nickName || null,
            phone: data.phone || null,
            previousPlaceOfStay: data.previousPlaceOfStay || null,
            nextDestination: data.nextDestination || null,
          },
          {
            headers: {
              Authorization: "Bearer " + state.token,
            },
          }
        )
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              window.location.href = "/confirm/" + state.bookingId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },
  guestUpdateReservation({ state, commit }, data) {
    if (data.occupation) {
      data.occupation = parseInt(data.occupation)
    }

    if (data.gender) {
      data.gender = parseInt(data.gender)
    }

    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .put(
          window.appApiUrl + "reserved/person/" + data.id,
          {
            name: data.name || undefined,
            address: data.address || null,
            occupation: data.occupation || null,
            otherOccupationName: data.otherOccupationName,
            postcode: data.postcode || null,
            birthDate: data.birthDate || null,
            gender: data.gender || null,
            nickName: data.nickName || null,
            phone: data.phone || null,
            previousPlaceOfStay: data.previousPlaceOfStay || null,
            nextDestination: data.nextDestination || null,
          },
          {
            headers: {
              Authorization: "Bearer " + state.guestToken,
            },
          }
        )
        .then((res) => {
          console.log(res)
          // commit('setGuestCurrent', data)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.guestReserve) {
              window.location.href =
                "/change-password/" +
                state.guestReserve.rsvId +
                "?userId=" +
                state.guestReserve.userId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK006
  deletePerson({ state, commit }, data) {
    console.log(state)
    return new Promise((resolve, reject) => {
      axios
        .delete(window.appApiUrl + "reserved/person/" + data.id, {
          headers: {
            Authorization: "Bearer " + state.token,
          },
        })
        .then((res) => {
          if (res.data.deleted) {
            commit("removeGuest", data.id)
          }
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              window.location.href = "/confirm/" + state.bookingId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK003
  personLogin({ commit }, data) {
    commit("guestLogout")
    return new Promise((resolve, reject) => {
      axios
        .post(window.appApiUrl + "reserved/personLogin/" + data.rsvId, data)
        .then((res) => {
          axios
            .get(window.appApiUrl + "reserved/user", {
              headers: {
                Authorization: "Bearer " + res.data.access_token,
              },
            })
            .then((resRsv) => {
              const persons = resRsv.data.persons
              const personItem = persons.find((item) => item.id == data.id)
              if (personItem) {
                console.log("xxx")
                commit("guestToken", {
                  id: data.id,
                  access_token: res.data.access_token,
                  ...personItem,
                })
                resolve(resRsv)
              } else {
                throw "Guest is not existed or deleted!"
              }
            })
            .catch((err) => {
              console.log(err)
              reject(err)
            })
        })
        .catch((err) => {
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK007
  //
  // 入力済みの宿泊者情報を消して、パスワードを再発行する。**token を必ず渡す**
  // （同じ予約の代表者トークン、または本人のパスワード認証済みトークン）。
  //
  // 2026-09-19、盗まれた宿泊者IDでこの API が無認証で叩かれ、16人分の宿泊者情報が消された
  // （DMS-1262）。無認証の呼び出しは廃止する（DMS-1268-H。サーバーは設定で 404 にする）ので、
  // このアプリからは呼ばない。パスワードを忘れた同行者は、代表者に招待を送り直してもらう。
  //
  // パスワードはサーバーが生成するので、呼び出し側からは指定しない（指定しても無視される）。
  personReset({ state }, data) {
    console.log(state)
    const headers = { "X-Client-Version": CLIENT_VERSION }
    if (data.token) {
      headers.Authorization = "Bearer " + data.token
    }
    return new Promise((resolve, reject) => {
      axios
        .post(
          window.appApiUrl + "reserved/personReset/" + data.id,
          {},
          { headers }
        )
        .then((res) => {
          console.log("api-log:", res)
          resolve(res)
        })
        .catch((err) => {
          console.log(err)
          reject(err)
        })
    })
  },

  // BOK011
  getProperties({ commit }, data) {
    return new Promise((resolve, reject) => {
      axios
        .get(window.appApiUrl + "reserved/property/" + data.bookingId)
        .then((res) => {
          //   // THIS IS FOR TEST
          //   res.data.inputField.notOtherThanOwner = true
          //   // UNTIL HERE.
          commit("setProperties", res.data)
          resolve(res)
        })
        .catch((err) => {
          console.log(err)
          reject(err)
        })
    })
  },

  // Not using api
  //
  // 代表者の代理入力で、編集する同行者を返す。
  //
  // 以前はパスワード未設定の同行者に、ここで無認証の personReset を呼んでパスワードを
  // 発行していた。代理入力は同行者のパスワードを使わない（表示も送信もしない）ので、
  // 発行は副作用でしかなかった。招待（DMS-1268-F）で入る同行者はパスワードが未設定のままなので、
  // 代理入力のたびに無認証の呼び出しが出ていた（本番の無認証 personReset の大半。DMS-1268-H）。
  async getUser({ state }, userId) {
    return state.reserve_guests.find((guest) => guest.id == userId)
  },

  //EXT007
  //
  // OCR。画像 GET と同じ資格情報（代表者は token、同行者は guestToken）を付ける（DMS-1265）。
  // サーバーは資格情報のある呼び出しだけを通す（identityImage クラスの enforce）。
  // 対象は宿泊者IDで渡し、API がストレージから直接読む。旧形式の {url} は互換のためだけに
  // 残っていて、全クライアントがこの形になったら撤去される。
  detectPassport({ state }, { bookingUserId, guest }) {
    const token = guest ? state.guestToken : state.token
    return new Promise((resolve, reject) => {
      axios
        .post(
          window.appApiUrl + "external/passportDetection",
          { bookingUserId },
          { headers: authHeaders(token) },
        )
        .then((response) => {
          resolve(response)
        })
        .catch((err) => reject(err))
    })
  },

  saveDefaultGuestInfo({ state }) {
    const saveProps = [
      "name",
      "birthday",
      "year",
      "month",
      "date",
      "gender",
      "email",
      "address",
      "postcode",
      "occupation",
    ]
    const toSave = saveProps.reduce((result, prop) => {
      result[prop] = state.currentUser[prop]
      return result
    }, {})

    localStorage.defaultGuestInfo = JSON.stringify(toSave)
  },

  loadDefaultGuestInfo({ commit, state }) {
    const data = localStorage.defaultGuestInfo
    if (data) {
      const info = JSON.parse(data)
      const copy = { ...state.currentUser }
      Object.keys(info).forEach((key) => {
        // if already has a value (input) use that
        if (info[key] && !copy[key]) {
          copy[key] = info[key]
        }
      })
      commit("setCurrentUser", copy)
    }
  },

  // 署名の取得・保存（CreateSignature.vue）は**代表者のステッパー（InfoStep）だけ**が使う。
  // InfoStep の isGuest は「代表者が同行者の分を代理入力している」印で、端末の資格情報は
  // どちらの場合も代表者の state.token（同行者の行は ownerProxy で扱える）。同行者自身の
  // ステッパー（GuestStep）に署名ステップは無い。以前は guestToken 固定だったため、代表者の
  // 端末では空のトークン（"Bearer " だけ）で送っていた（DMS-1265）。
  // BOK030
  guestUploadSignature({ state, commit }, { id, file }) {
    const form = new FormData()
    form.append("signatureImageFile", file)
    const token = state.token
    return new Promise((resolve, reject) => {
      axios
        .post(window.appApiUrl + "reserved/personSignature/" + id, form, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...authHeaders(token),
          },
        })
        .then((res) => {
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            window.location.href = signatureUnauthorizedRedirect(state)
          }
          reject(err)
        })
    })
  },

  // BOK029
  downloadSignature({ state, commit }, { id }) {
    const token = state.token
    return new Promise((resolve, reject) => {
      axios
        .get(window.appApiUrl + "reserved/personSignature/" + id, {
          responseType: "blob",
          headers: authHeaders(token),
        })
        .then((res) => {
          resolve(res.data)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            window.location.href = signatureUnauthorizedRedirect(state)
          }
          reject(err)
        })
    })
  },

  // BOK095
  completePreCheckIn({ state, commit }, { preCheckedInAt }) {
    return new Promise((resolve, reject) => {
      axios
        .patch(
          `${window.appApiUrl}reserved/${state.bookingId}/preCheckIn`,
          { preCheckedInAt },
          {
            headers: {
              Authorization: "Bearer " + state.token,
            },
          }
        )
        .then((res) => {
          console.log(res)
          resolve(res)
        })
        .catch((err) => {
          if (err.response.status == 401) {
            commit("clearForm")
            if (state.bookingId) {
              window.location.href = "/confirm/" + state.bookingId
            } else {
              window.location.href = "/error-page"
            }
          }
          console.log(err)
          reject(err)
        })
    })
  },
}
